/**
 * Small per-owner state of this page that is not a request (batch 8 T12c1, #3033).
 *
 *  - Commands without a durable identity (cancel, resolve, settings save): if the outcome is unknown, the command is NOT repeated.
 *    The screen refreshes the state and shows that the outcome is unknown until the user acknowledges it. The mark lives here (in
 *    memory, per owner and target), so closing a dialog or remounting a panel cannot hide it. It is not persisted: after a reload the
 *    screen shows the state the server has, which is the truth the mark points at.
 *  - The document epoch: any change this page makes that can alter a purchase order document (a settings save, a queued dispatch)
 *    bumps it, and a preview taken at an older epoch is no longer a review of what the server would send now.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { ownerId, type DispatchOwner } from './dispatchSession';

export type UnconfirmedKind = 'cancel' | 'resolve' | 'settings';

export interface UnconfirmedCommand {
  readonly kind: UnconfirmedKind;
  readonly target: string;
  readonly at: string;
}

const MAX_MARKS_PER_OWNER = 50;

const marks = new Map<string, UnconfirmedCommand[]>();
const epochs = new Map<string, number>();
const listeners = new Set<() => void>();
const EMPTY: readonly UnconfirmedCommand[] = Object.freeze([]);

const notify = (): void => {
  for (const listener of [...listeners]) listener();
};
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function addUnconfirmed(owner: DispatchOwner, kind: UnconfirmedKind, target: string): void {
  const id = ownerId(owner);
  const current = marks.get(id) ?? [];
  if (current.some((mark) => mark.kind === kind && mark.target === target)) return;
  // Bounded: the oldest mark goes first, the newest is always kept.
  const next = [...current, { kind, target, at: new Date().toISOString() }].slice(
    -MAX_MARKS_PER_OWNER
  );
  marks.set(id, next);
  notify();
}

/** Only the user's explicit acknowledgement removes a mark. */
export function acknowledgeUnconfirmed(
  owner: DispatchOwner,
  kind: UnconfirmedKind,
  target: string
): void {
  const id = ownerId(owner);
  const current = marks.get(id);
  if (!current) return;
  const next = current.filter((mark) => !(mark.kind === kind && mark.target === target));
  if (next.length === current.length) return;
  if (next.length === 0) marks.delete(id);
  else marks.set(id, next);
  notify();
}

/** A synchronous read, for the moment of an action (never a value captured by a render). */
export function hasUnconfirmed(
  owner: DispatchOwner,
  kind: UnconfirmedKind,
  target: string
): boolean {
  return (marks.get(ownerId(owner)) ?? EMPTY).some(
    (mark) => mark.kind === kind && mark.target === target
  );
}

export function useUnconfirmed(
  owner: DispatchOwner | null,
  kind: UnconfirmedKind,
  target: string | null
): UnconfirmedCommand | null {
  const snapshot = useCallback(
    () => (owner === null || target === null ? EMPTY : (marks.get(ownerId(owner)) ?? EMPTY)),
    [owner, target]
  );
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return list.find((mark) => mark.kind === kind && mark.target === target) ?? null;
}

export function getDocumentEpoch(owner: DispatchOwner | null): number {
  return owner === null ? 0 : (epochs.get(ownerId(owner)) ?? 0);
}

export function bumpDocumentEpoch(owner: DispatchOwner): void {
  const id = ownerId(owner);
  epochs.set(id, (epochs.get(id) ?? 0) + 1);
  notify();
}

export function useDocumentEpoch(owner: DispatchOwner | null): number {
  const snapshot = useCallback(() => getDocumentEpoch(owner), [owner]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Test support. */
export function resetDispatchLocalStateForTests(): void {
  marks.clear();
  epochs.clear();
  notify();
}
